<template>
  <div v-if="errorResponse">
    <WarningPanel
      :msg="errorResponse"
      :severe="true"
      :title="'There\'s a problem...'"
    />
  </div>
  <div v-else class="page-wrapper-new">
    <BackButton class="back-link" />

    <!--  Package summary  -->
    <PackageSummaryNew
      :hide-unwanted-products="false"
      :inline-editing="false"
      :on-toggle-products="handleToggleProducts"
      :on-toggle-tv-licence="handleToggleTvLicence"
      @reapply-discount="applyDiscountCode"
      :selected-products="selectedProducts"
      class="package-summary"
      thin
    >
      <template #summary-header>
        <!--price per person -->
        <div class="package-summary-header">
          <div class="your-package" data-testid="page-title">Your price is</div>
          <div v-show="!isDiscountValidating" id="price-discount-section">
            <section class="price-display">
              <div
                v-if="discountApplied"
                class="nondiscounted-price"
                data-testid="nondiscounted-price"
              >
                £{{ nonDiscountedPricePerPersonPerWeek }}
              </div>
              <div
                :class="discountApplied ? 'price__discount-applied' : 'price'"
                data-testid="final-ppppw"
              >
                £{{ pricePerPersonPerWeek }}
              </div>
            </section>
            <div
              :class="{
                'mb-30': !shouldShowVouchers,
              }"
              class="pppw"
            >
              <span>Per person per week based on a 12 month contract.</span>
              <monthly-price-info
                :formatted-price-per-month="parseFloat(formattedPricePerMonth)"
              />
            </div>
            <!--        Voucher -->
            <discount-activation
              v-if="shouldShowVouchers"
              v-model.trim="discountCode"
              :summary-control="false"
              @apply="applyDiscountCode"
              @clear="clearDiscountCode"
            />
          </div>
          <div
            v-if="isDiscountValidating"
            id="price-discount-section"
            style="padding-top: 11rem; padding-bottom: 11rem;"
          >
            <i
              class="fa-solid fa-spinner-third fa-spin fa-2x"
              data-testid="voucher-loading-animation"
            ></i>
          </div>
        </div>

        <!--    continue btn -->
        <button
          class="btn btn-summary-next btn-summary-next--header"
          style="margin-bottom: 24px;"
          data-testid="summary-next-button"
          @click="join"
        >
          Continue with this price
        </button>

        <div class="property-info property-info--top">
          <hr class="property-info__divider" style="margin-bottom: 16px;" />
          <h3
            class="package-summary-header package-summary-header--property-info"
            style="margin-bottom: 8px;"
          >
            Property information
          </h3>
          <div class="property-info__content">
            <div
              class="property-info__item property-info__item--people"
              data-testid="housemate-count"
            >
              <i class="fa-solid fa-user property-info-icon"></i>
              {{ housemateCount }} people
            </div>

            <div
              class="address-or-postcode property-info__item"
              data-testid="address"
            >
              <i class="fa-solid fa-house property-info-icon"></i>
              {{ addressOrPostcode }}
            </div>
          </div>
        </div>

        <hr class="property-info__divider" />

        <h3
          class="package-summary-header package-summary-header--property-info"
          style="text-align: left; padding: 16px 0;"
        >
          Your package includes
        </h3>
      </template>
    </PackageSummaryNew>

    <div class="property-info property-info--footer">
      <hr class="property-info__divider" />
      <h3
        class="package-summary-header package-summary-header--property-info"
        style="margin: 16px 0 8px 0;"
      >
        Property information
      </h3>
      <div class="property-info__content">
        <div
          class="property-info__item property-info__item--people"
          data-testid="housemate-count"
        >
          <i class="fa-solid fa-user property-info-icon"></i>
          {{ housemateCount }} people
        </div>

        <div
          class="address-or-postcode property-info__item"
          data-testid="address"
        >
          <i class="fa-solid fa-house property-info-icon"></i>
          {{ addressOrPostcode }}
        </div>
      </div>
    </div>
    <!--    Footer buttons-->
    <button
      class="btn btn-summary-next"
      @click="join"
      style="margin-bottom: -8px;"
      ref="nextButtonWrapper"
    >
      Continue with this price
    </button>
    <button
      class="save-cta"
      data-testid="package-share-button"
      @click="showSaveShareModal"
    >
      <i class="fa-regular fa-floppy-disk" style="color: #00A2E1;"></i>
      Save or share this price
    </button>

    <div
      class="continue-price__wrapper continue-price__wrapper--shadow continue-price__wrapper--sticky"
      ref="nextButtonWrapperSticky"
      v-show="isSticky"
    >
      <div class="continue-price__inner">
        <p style="text-align: center; font-size: 14px; margin: 16px 0 10px 0;">
          £{{ pricePerPersonPerWeek }} per person per week
        </p>
        <button
          class="btn btn-summary-next"
          style="margin-bottom: 0;"
          @click="join"
        >
          Continue with this price
        </button>
      </div>
    </div>

    <PackageSaveShareOverlay
      v-if="saveShareOverlayVisible"
      @close-panel="toggleSaveSharePanel"
    />

    <hr class="property-info__divider" />

    <div class="footer-info">
      <h3 class="package-summary-header">
        Why UniHomes?
      </h3>
      <span>
        <i class="fa-solid fa-circle-check circle-chk"></i>
        Everything is bundled into one payment
      </span>
      <span>
        <i class="fa-solid fa-circle-check circle-chk"></i>
        Everyone pays their fair share
      </span>
      <span>
        <i class="fa-solid fa-circle-check circle-chk"></i>
        No hidden costs or fees
      </span>
    </div>

    <!--    Trustpilot -->
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import WarningPanel from "@/components/WarningPanel";
import DiscountActivation from "@/components/DiscountActivation.vue";
import {
  initialDiscountState,
  initialPackagePrice,
} from "@/store/initialState";
import MonthlyPriceInfo from "@/components/MonthlyPriceInfo.vue";
import PackageSummaryNew from "@/components/PackageSummaryNew.vue";
import PackageSaveShareOverlay from "@/components/PackageSaveShareOverlay.vue";
import { findDiscountCode } from "@/utility/discount";

export default {
  name: "ShortSummaryTest",
  data() {
    return {
      saveShareOverlayVisible: false,
      errorResponse: "",
      selectedProducts: [],
      isSticky: true,
    };
  },
  computed: {
    ...mapState(["agent", "discount"]),
    ...mapGetters({
      housemateCount: "housemateCount",
      postcode: "postcode",
      hasUsedDiscount: "hasUsedDiscount",
      hasAddress: "hasAddress",
      addressLine1: "addressLine1",
      address: "getCustomerAddressString",
      shouldShowVouchers: "shouldShowVouchers",
    }),
    nonDiscountedPricePerPersonPerWeek() {
      return (this.$store.getters.nonDiscountedPricePerPerson / 100).toFixed(2);
    },
    pricePerPersonPerWeek() {
      return (this.$store.getters.pricePerPerson / 100).toFixed(2);
    },
    formattedPricePerMonth() {
      return this.$store.getters.formattedPricePerMonth;
    },
    addressOrPostcode() {
      if (this.hasAddress) {
        return this.address;
      } else {
        return this.postcode;
      }
    },
    discountCode: {
      get() {
        return this.$store.state.discount.code;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { code: value });
      },
    },
    discountApplied: {
      get() {
        return this.$store.state.discount.isValid;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValid: value });
      },
    },
    isDiscountValidating: {
      get() {
        return this.$store.state.discount.isValidating;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValidating: value });
      },
    },
  },
  methods: {
    ...mapActions([
      "getProducts",
      "getReferralMessage",
      "getAffiliateMessage",
      "activateDiscount",
      "updatePreSignupLeadProductData",
    ]),
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setProductOption: "SET_PRODUCT_OPTION",
      setPostcode: "SET_POSTCODE",
      setHousemateCount: "SET_HOUSEMATE_COUNT",
      setProducts: "SET_PRODUCTS",
      setPackagePrice: "SET_PACKAGE_PRICE",
      setIsViaShareLink: "IS_VIA_SHARELINK",
      hideBackButton: "HIDE_BACK_BTN",
      setDiscountState: "SET_DISCOUNT_STATE",
      setLeadTenantAddress: "SET_LEAD_TENANT_ADDRESS",
    }),
    handleToggleProducts() {
      this.$nextTick(() => {
        if (this.discountApplied) {
          this.applyDiscountCode();
        }
      });
    },
    showSaveShareModal() {
      this.trackGoogleEvent("Click Share", "Get Price", "Share price");
      this.toggleSaveSharePanel();
    },
    toggleSaveSharePanel() {
      this.saveShareOverlayVisible = !this.saveShareOverlayVisible;
    },
    join() {
      this.trackGoogleEvent(
        "Click Create Account",
        "Create Account",
        "Create Account",
      );

      if (this.hasAddress) {
        this.$router.push("/your-details");
      } else {
        this.$router.push("/your-address");
      }
      if (this.hasAddress) {
        this.$router.push("/your-details");
      } else {
        this.$router.push("/your-address");
      }
    },
    applyDiscountCode() {
      this.$set(this, "isDiscountValidating", true);

      this.activateDiscount({
        voucherCode: this.$store.state.discount.code,
        selectedProducts: this.$store.getters.selectedProductPriceIds,
        housemateCount: this.$store.state.housemateCount,
        leadId: this.$store.state.leadId,
      })
        .then(({ data }) => {
          this.$set(this, "discountApplied", true);

          this.$store.commit("SET_PACKAGE_PRICE", {
            fullPriceAmount: data.priceBeforeDiscount,
            discountedPriceAmount: data.priceAfterDiscount,
          });

          this.$store.commit("SET_DISCOUNT_STATE", {
            description: data.message,
            errorMsg: "",
          });

          this.trackGoogleEvent(
            "Voucher applied successfully",
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .catch((err) => {
          this.$store.commit("SET_DISCOUNT_STATE", {
            errorMsg:
              err.response.data.message ||
              "Invalid voucher code. Check and try again.",
          });

          this.trackGoogleEvent(
            `Voucher applied failed`,
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .finally(() => {
          this.$set(this, "isDiscountValidating", false);
        });
    },
    handleToggleTvLicence() {
      // Keep nextTick in place to allow store to be updated prior to retrieving the updated selectedProductPriceIds, for discount code implementation
      this.$nextTick(() => {
        if (this.discountApplied) {
          this.applyDiscountCode();
        }
      });
    },
    clearDiscountCode() {
      this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());
      this.$store.commit("SET_DISCOUNT_STATE", initialDiscountState());
    },
    loadShareLinkData() {
      const addressGuid = this.$route.query.address;

      this.getProducts({
        addressGuid,
        postcode: this.$route.query.postcode,
        housemateCount: this.$route.query.housemates,
        voucherCode: this.$route.query.voucherCode,
        hash: this.$route.query.hash,
      })
        .then(({ data }) => {
          // ensure errorResponse is reset
          this.errorResponse = "";

          if (data.address) {
            this.setLeadTenantAddress(data.address);
            this.setPostcode(data.address.postcode);
          }

          if (data.existingAccountGUID) {
            this.$router.push({
              name: "account-already-exists",
              params: { guid: data.existingAccountGUID },
            });
          } else {
            // set the store state
            if (this.$route.query.postcode) {
              this.setPostcode(this.$route.query.postcode);
            }
            this.setHousemateCount(this.$route.query.housemates);
            this.setProducts(data.products);

            if (data.priceBeforeDiscount) {
              this.nonDiscountedPricePerPersonPerWeekFormatted = (
                data.priceBeforeDiscount / 100
              ).toFixed(2);
            }

            if (data.priceAfterDiscount) {
              this.pricePerPersonPerWeekFormatted = (
                data.priceAfterDiscount / 100
              ).toFixed(2);
            }
            // Because the app has been entered mid-journey, we need to set a variable
            // that controls what the "back/editProduct" button directs to
            this.setIsViaShareLink(true);

            // Filter the query parameters ensuring the selected products are stored in state
            Object.keys(this.$route.query).forEach((key) => {
              // This mutation performs a find on the products array based on the
              // query key, so it's fine to pass the whole query parameter set
              this.setProductOption({
                type: key,
                code: this.$route.query[key],
              });
            });

            // If referral code, commit it to the store...
            if (this.$route.query.ref) {
              this.getReferralMessage(this.$route.query.ref);
            }

            // If affiliate code, commit it to the store...
            if (this.$route.query.aff) {
              this.getAffiliateMessage(this.$route.query.aff);
            }

            if (this.$route.query.voucherCode) {
              this.setDiscountState({
                inputRevealed: true,
                code: this.$route.query.voucherCode,
              });
              this.applyDiscountCode();
            }

            if (Object.hasOwn(data, "discount")) {
              this.$store.commit("SET_DISCOUNT_STATE", {
                isValid: data.discount.isValid,
                inputRevealed: data.discount.inputRevealed,
                offer: data.discount.offer,
                aff: data.discount.aff,
                code: data.discount.code,
              });

              if (Object.hasOwn(data, "leadId")) {
                this.$store.commit("SET_LEAD_ID", data.leadId);
              }
            }
          }
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse =
              "Network error accessing the Split The Bills API";
          } else {
            this.errorResponse = error.response.data.messages[0];
          }
        })
        .finally(() => {
          this.hideLoadingPanel();
        });
    },
    toggleStickyFooter() {
      // hide sticky button if it's below form button
      const formButton = this.$refs.nextButtonWrapper;
      const formButtonRect = formButton.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      // adding 16px here, same as the button's bottom padding, which makes the transition a lot smoother
      this.isSticky = !(formButtonRect.bottom + 16 < windowHeight);
    },
  },
  created() {
    this.clearDiscountCode();

    // Check if this is a share link (has required query parameters)
    const hasAddressOrPostcode =
      this.$route.query.address || this.$route.query.postcode;
    const hasHousemates = this.$route.query.housemates;

    // Only run share link logic if we have the required parameters
    if (hasAddressOrPostcode && hasHousemates) {
      this.loadShareLinkData();
    } else {
      this.hideLoadingPanel();
    }
  },
  mounted() {
    findDiscountCode(this.$route.query, this.$store);
    this.updatePreSignupLeadProductData({
      offerCode: this.$store.state.discount.offer,
      affiliateCode: this.$store.state.discount.aff,
      referralCode: this.$route.query.ref,
    });
    window.addEventListener("scroll", this.toggleStickyFooter);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.toggleStickyFooter);
  },
  components: {
    PackageSummaryNew,
    MonthlyPriceInfo,
    DiscountActivation,
    PackageSaveShareOverlay,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.page-wrapper-new {
  display: flex;
  width: 500px;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  border-radius: 24px;
  background: #fff;
  margin-bottom: 24px;
  margin-top: 3rem;

  @include media("<large") {
    width: auto;
    padding: 24px 16px;
  }
}

.package-summary {
  background: $white;
  text-align: center;

  .edit-pkg-button {
    margin: 2rem 0;
    text-decoration: underline;
    font-size: 20px;
    color: $brand-green;
  }
}

.package-summary-header {
  @extend h1;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  margin: 0;
  color: $brand-blue;

  &--property-info {
    text-align: left;
    font-size: 20px;

    @include media("<large") {
      font-size: 16px;
    }
  }

  .your-package {
    color: $brand-blue;
    padding: 0 0 8px 0;
  }

  .price-display {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.3em;
    margin-bottom: 0.5em;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;
    color: $brand-dark-blue;
    text-align: center;
  }

  .nondiscounted-price {
    color: $brand-dark-blue;
    text-decoration: line-through;
    font-size: $form-font-size;
    font-weight: $font-medium;
  }

  .price {
    font-size: 34px;
    font-weight: $font-medium;

    &__discount-applied {
      color: $brand-dark-blue;
    }
  }

  .nondiscounted-price + .price {
    line-height: 0.9;
    margin-bottom: 4px;
  }

  .tagline {
    color: $text;
    font-weight: 300;
  }

  .pppw span {
    color: #3b4143;
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  .tagline {
    margin-top: 1rem;
    font-size: 20px;
    font-weight: $font-regular;
  }
}

.--main-cta {
  @include media("<medium") {
    margin-bottom: $spacing-small;
  }
}

.btn-summary-next {
  display: flex;
  height: 60px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  width: 100%;
  border-radius: 8px;
  margin: 0;
  font-size: 18px;
  line-height: 27px;

  &--header {
    @include media("<large") {
      display: none;
    }
  }
}

.save-action {
  text-align: center;
}

.save-cta {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 60px;
  padding: 4px 24px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  width: 100%;

  &:hover {
    border: 1px solid #afb0b4;
  }
}

.summary-header {
  text-align: center;
  margin-bottom: 0;
  font-size: 18px;
  font-weight: $font-regular;

  .address-or-postcode {
    color: $brand-orange;
  }
}

.property-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  margin: 16px 0;

  h3 {
    text-align: left;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-self: stretch;
    align-items: flex-start;
  }

  &__item {
    color: #3b4143;
    font-size: 16px;
    font-weight: 400;
    line-height: normal;
    display: flex;
    align-items: center;
    gap: 8px;

    &--people {
      gap: 14px;
    }
  }

  &--top {
    margin-top: 0;

    @include media("<large") {
      display: none;
    }
  }

  &--footer {
    margin: 0;

    @include media(">large") {
      display: none;
    }
  }
}

.property-info__divider {
  border: none;
  //border-top: 1px solid #e5e7eb;
  margin: 0;
  width: 100%;
}

.footer-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  h3 {
    color: $brand-dark-blue;
  }

  span {
    color: #3b4143;
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
}

.circle-chk {
  color: #0e9f6e;
  padding: 0 8px 0 0;
}
</style>

<style lang="scss">
.agent-summary {
  padding-top: $spacing-small;

  @include media(">=medium") {
    margin: 0 $spacing-medium;
  }
}

.mb-30 {
  margin-bottom: 30px;
}

.pppw span {
  display: block;
  margin-bottom: 4px;
}

.pppw span:last-child {
  margin-bottom: 0; /* Remove gap after the last line */
}

.btn--big {
  width: 100%;
  height: 100%;
  margin: 0 auto 2rem auto;
  min-height: 6rem;
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
}

.continue-price {
  &__wrapper {
    font-size: 1.2rem;

    @include media("<medium") {
      padding-bottom: 1.6rem;
      display: flex;
      flex-direction: column;
      width: 100%;
      position: absolute;
      background-color: white;
      align-items: center;

      &--shadow {
        box-shadow: 0 -7px 7px -4px #dadada;
      }

      &--sticky {
        position: fixed;
        bottom: 0;
        z-index: 900;
      }
    }
  }

  &__inner {
    width: 100%;
    padding: 0 16px;
  }
}

.back-link {
  @include media(">large") {
    display: none;
  }
}
</style>
